import { useFormattedQuery } from '@lib/hooks/useFormattedQuery'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { ParsedUrlQueryInput } from 'node:querystring'
import { AnchorHTMLAttributes, FC, forwardRef, useMemo } from 'react'
import { UmbracoLink } from 'types/umbracoLink'
import { UrlObject } from 'url'
export type Url = string | UrlObject
export type LinkProps = {
  link?: UmbracoLink
  pathname?: boolean
  query?: ParsedUrlQueryInput | null | undefined
}

const Link: FC<LinkProps & AnchorHTMLAttributes<HTMLAnchorElement>> =
  forwardRef(({ link, children, pathname = true, query, ...props }, ref) => {
    const router = useRouter()
    const formattedQuery = useFormattedQuery(router)
    const { country, language } = router.query

    const isHomeLink = link?.url === '/home/'

    const urlParams = useMemo(() => {
      if (!link?.url) return {}
      // use a base so that URL() works with relative URLs
      const u = new URL(
        link.url,
        typeof window !== 'undefined' ? window.location.origin : 'http://dummy',
      )
      return Object.fromEntries(u.searchParams.entries())
    }, [link?.url])

    return link || query ? (
      link &&
      (link.url?.startsWith('#') ||
        link.type === 'EXTERNAL' ||
        link.url?.startsWith('mailto:') ||
        link.url?.startsWith('tel:') ||
        link.url?.startsWith('http') ||
        link.url?.startsWith('sms:')) ? (
        <a {...props} href={link.url} target={link.target}>
          {children}
        </a>
      ) : (
        <NextLink
          href={{
            pathname:
              pathname && link
                ? `/${language}/${country}${isHomeLink ? '/' : (link?.url?.split('?')[0] ?? '')}`
                : undefined,
            query: {
              ...query,
              ...formattedQuery,
              ...urlParams,
            },
          }}
          hrefLang={`${language}-${country.toString().toUpperCase()}`}
          target={link?.target ?? undefined}
          prefetch={false}
          {...props}
          data-link="next"
        >
          {children}
        </NextLink>
      )
    ) : (
      <div className={props.className}>{children}</div>
    )
  })
export default Link

Link.displayName = 'Link'
